import React from 'react';
import {
  Box,
  Typography,
  Button,
  Grid,
  useTheme,
  useMediaQuery,
  styled
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import studentImageLoanCardDashboard from '../../assets/svgIcons/studentImageLoanCardDashboard.svg';
import studentImageLoanCardDashboardMobile from '../../assets/svgIcons/studentImageLoanCardDashboardMobile.svg';
import loanCardBulletIcon from '../../assets/svgIcons/loanCardBulletIcon.svg';
import productImageDashboard from '../../assets/svgIcons/product-image-dashboard.svg';
import productImageDashboardMobile from '../../assets/svgIcons/pwa-loan-product-bg-img.svg';

interface LoanCardDashboardProps {
  handleClearData: () => void;
}

// --- Styled Components ---
const CardContainer = styled(Box)(({ theme }) => ({
  backgroundColor: '#f4f9faff',
  backgroundSize: 'cover', 
  backgroundPosition: 'center',
  backgroundRepeat: 'no-repeat',
  borderRadius: '10px',
  padding: '24px', 
  position: 'relative',
  border: '1.5px solid transparent',
  backgroundClip: 'padding-box',
  display: 'flex',
  alignItems: 'center',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0, right: 0, bottom: 0, left: 0,
    zIndex: -1,
    margin: '-1.5px',
    borderRadius: 'inherit',
    background: 'linear-gradient(to right, #00B3B5, #E98029)',
  },
  [theme.breakpoints.down('sm')]: {
    boxShadow: '0px 5px 20px 0px rgba(75, 70, 92, 0.24)',
  },
  [theme.breakpoints.up('sm')]: {
    boxShadow: '0px 4px 18px 0px rgba(75, 70, 92, 0.1)',
  },
}));

const FeatureItem: React.FC<{ text: string, isXsOrSm: boolean }> = ({ text, isXsOrSm }) => (
  <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 1.5 }}>
    <Box
      component="img"
      src={loanCardBulletIcon}
      sx={{
        mr: 1,
        mt: 0.4,
        width: isXsOrSm ? '12px' : '18px',
      }}
    />
    <Typography
      variant="body2"
      sx={{ 
        color: '#4B465C', 
        fontSize: isXsOrSm ? '11px' : '15px', 
        fontWeight: 400, 
        lineHeight: 1.2 
      }}
    >
      {text}
    </Typography>
  </Box>
);

const LoanCardDashboard: React.FC<LoanCardDashboardProps> = ({ handleClearData }) => {
  const theme = useTheme();
  const isXsOrSm = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();

  const handleApplyNow = () => {
    handleClearData();
    navigate("/application/apply-now");
  };

  const features: string[] = [
    "Higher loan limits",
    "Deferred payment options",
    "Interest-only payments during study",
    "Special rates for premier institutions"
  ];

  return (
    <CardContainer 
      sx={{
        backgroundImage: `url(${isXsOrSm ? productImageDashboardMobile : productImageDashboard})` 
      }}
    >
      <Grid container spacing={isXsOrSm ? 1 : 4} alignItems="center">
        <Grid item xs={12} md={6} sx={{ ml: isXsOrSm ? 0 : 5 }}>
          <Typography
            variant="h4"
            sx={{
              fontSize: isXsOrSm ? '20px' : '32px',
              fontWeight: 700,
              color: '#1F2233',
              mb: isXsOrSm ? 2 : 3,
            }}
          >
            Student Loan <span style={{ color: '#00B3B5' }}>International</span>
          </Typography>

          <Grid container spacing={isXsOrSm ? 1 : 0}>
            {features.map((feature, index) => (
              <Grid item xs={6} sm={6} key={index}> 
                <FeatureItem text={feature} isXsOrSm={isXsOrSm} />
              </Grid>
            ))}
          </Grid>

          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: isXsOrSm ? 'space-between' : 'flex-start',
            gap: 2,
            mt: isXsOrSm ? 1 : 4 
          }}>
            <Button
              variant="contained"
              onClick={handleApplyNow}
              sx={{
                backgroundColor: '#ff9f43',
                color: '#000',
                fontWeight: 700,
                fontSize: isXsOrSm ? '12px' : '15px',
                textTransform: 'none',
                borderRadius: '8px',
                px: isXsOrSm ? 2 : 5,
                py: 1.5,
                boxShadow: '0px 4px 12px rgba(255, 159, 67, 0.4)',
                '&:hover': { backgroundColor: '#FF9F43' }
              }}
            >
              Apply Now
            </Button>
          </Box>
        </Grid>

        {/* Right Side Image for Desktop */}
        {!isXsOrSm && (
          <Grid item md={5} sx={{ display: 'flex', justifyContent: 'flex-end', pr: 2 }}>
            <Box
              component="img"
              src={studentImageLoanCardDashboard}
              sx={{
                width: '100%',
                maxWidth: '380px',
                height: 'auto',
                objectFit: 'contain'
              }}
            />
          </Grid>
        )}
      </Grid>

      {/* Background Decorative Circle */}
      <Box
        sx={{
          position: 'absolute',
          bottom: -20,
          left: -20,
          width: 80,
          height: 80,
          border: '8px solid rgba(0, 179, 181, 0.1)',
          borderRadius: '50%',
          zIndex: 0
        }}
      />
    </CardContainer>
  );
};

export default LoanCardDashboard;
